import React from 'react';
import {
  Box,
  Checkbox,
  Flex,
  FormLabel,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Text,
  Textarea,
  Tooltip,
  Icon
} from '@chakra-ui/react';
import { MdInfoOutline } from 'react-icons/md';
import InputComponent from '@components/Input';
import { INPUT_FIELD_TYPE } from '@components/DynamicFormField/constant';
import SearchableSelect from '@components/DynamicFormField/SearchableSelect';
import ArrayDragDropManager from '@components/SettlementServiceDashboard/ArrayDragDropManager';

// Recent Changes :: Change fieldType to inputType everywhere in the form config
// 1. fieldType is renamed to type and dataType is renamed to inputType
// 2. Added a new field - Name
// 3. Width for input component
const DynamicFormField = ({
  fieldConfig,
  data,
  handleChange,
  ignoreScroll = false
}) => {
  const renderField = () => {
    switch (fieldConfig.fieldType) {
      case INPUT_FIELD_TYPE.INPUT:
        return (
          <InputComponent
            id={fieldConfig.value}
            size={fieldConfig.size || 'md'}
            type={fieldConfig?.inputType || 'text'}
            rounded="md"
            placeholder={
              fieldConfig.placeholder || `Enter ${fieldConfig?.label} here...`
            }
            name={fieldConfig.name}
            value={data[fieldConfig.name] ?? ''}
            handleChange={handleChange}
            required={fieldConfig.isRequired}
            min={fieldConfig?.minValue}
            maxLength={fieldConfig?.maxLength}
            width={fieldConfig?.width}
            defaultValue={fieldConfig.defaultValue}
            disabled={!fieldConfig.isEditable}
            ignoreScroll={ignoreScroll}
          />
        );

      case INPUT_FIELD_TYPE.TEXTAREA:
        return (
          <Textarea
            id={fieldConfig.value}
            className="scroll-sm"
            size="sm"
            rounded="lg"
            fontSize={'md'}
            style={{
              border: 0,
              background: '#414141',
              borderRadius: '12px',
              gap: '12px',
              margin: '0'
            }}
            focusBorderColor="brand.400"
            placeholder={`${
              fieldConfig.placeholder || `Enter ${fieldConfig?.label} here...`
            }`}
            _placeholder={{
              color: 'gray.500'
            }}
            name={fieldConfig.name}
            value={data[fieldConfig.name] ?? ''}
            onChange={handleChange}
            required={fieldConfig.isRequired}
            defaultValue={fieldConfig.defaultValue}
          />
        );

      case INPUT_FIELD_TYPE.CHECKBOX:
        return (
          <Checkbox
            id={fieldConfig.value}
            size={fieldConfig.size || 'md'}
            colorScheme="brand"
            name={fieldConfig.name}
            isChecked={!!data[fieldConfig.name]}
            onChange={handleChange}
            defaultValue={fieldConfig.defaultValue}
            required={fieldConfig.isRequired}
          >
            {fieldConfig.label}
          </Checkbox>
        );

      case INPUT_FIELD_TYPE.RADIO:
        return (
          <RadioGroup
            id={fieldConfig.value}
            name={fieldConfig.name}
            value={data[fieldConfig.name] ?? ''}
            onChange={(val) =>
              handleChange({ target: { name: fieldConfig.value, value: val } })
            }
            isRequired={fieldConfig.isRequired}
            defaultValue={fieldConfig.defaultValue}
          >
            <Flex gap={4} flexDirection={fieldConfig.direction || 'row'}>
              {fieldConfig.options.map((option, idx) => (
                <Radio key={idx} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </Flex>
          </RadioGroup>
        );

      case INPUT_FIELD_TYPE.SWITCH:
        return (
          <Switch
            id={fieldConfig.value}
            name={fieldConfig.name}
            size={fieldConfig.size}
            colorScheme="brand"
            isChecked={!!data[fieldConfig.name]}
            onChange={handleChange}
            required={fieldConfig.isRequired}
            defaultValue={fieldConfig.defaultValue}
            isDisabled={!fieldConfig.isEditable}
            isReadOnly={!fieldConfig.isEditable}
          />
        );

      case INPUT_FIELD_TYPE.SELECT:
        return (
          <Select
            id={fieldConfig.value}
            name={fieldConfig.name}
            size={fieldConfig.size || 'md'}
            colorScheme="brand"
            bg="altGray.200"
            color="shadedGray.50"
            focusBorderColor="brand.400"
            style={{
              border: 0,
              background: '#414141',
              borderRadius: '12px',
              gap: '12px',
              margin: '0'
            }}
            placeholder={fieldConfig.placeholder || 'Select an option'}
            value={data[fieldConfig.name] ?? ''}
            defaultValue={fieldConfig.defaultValue}
            onChange={handleChange}
            required={fieldConfig.isRequired}
            focusBorderColor="brand.400"
            color="white"
            rounded="lg"
            w={'fit-content'}
            style={{
              border: 0,
              background: '#414141',
              borderRadius: '12px',
              gap: '12px',
              margin: '0'
            }}
            _placeholder={{
              color: 'gray.500'
            }}
          >
            {fieldConfig.options.map((option, idx) => (
              <option
                key={idx}
                value={option.value}
                disabled={option.isDisabled}
                style={{
                  background: '#414141',
                  color: 'white'
                }}
              >
                {option.label}
              </option>
            ))}
          </Select>
        );

      case INPUT_FIELD_TYPE.SEARCHABLE_SELECT:
        return (
          <SearchableSelect
            name={fieldConfig.name}
            options={fieldConfig.options || []}
            value={data[fieldConfig.name] ?? ''}
            placeholder={fieldConfig.placeholder || 'Select an option'}
            onChange={handleChange}
            isDisabled={!fieldConfig.isEditable}
            emptyMessage={fieldConfig.emptyMessage}
          />
        );

      case INPUT_FIELD_TYPE.DRAG_DROP:
        return (
          <ArrayDragDropManager
            availableOptions={fieldConfig.options || []}
            value={data[fieldConfig.name] || []}
            onChange={(value) =>
              handleChange({ target: { name: fieldConfig.name, value } })
            }
            label={fieldConfig.label}
            placeholder={fieldConfig.placeholder}
            hideLabel={true}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      {fieldConfig.type !== INPUT_FIELD_TYPE.CHECKBOX && (
        <Flex
          w="100%"
          justifyContent="flex-start"
          height="100%"
          alignItems="center"
        >
          <FormLabel
            alignSelf="flex-end"
            fontSize="0.75rem"
            fontWeight="500"
            mb={0}
            lineHeight="2rem"
            pr={2}
            size={fieldConfig?.size || 'md'}
            htmlFor={fieldConfig.name}
            color={fieldConfig?.labelColor || 'inherit'}
          >
            {fieldConfig.label}
            {fieldConfig.isRequired && (
              <Text as="span" color="red.500" fontSize="sm">
                *
              </Text>
            )}
            {fieldConfig.tooltipLabel && (
              <Tooltip
                label={fieldConfig.tooltipLabel}
                fontSize="xs"
                bg="gray.700"
                color="white"
                borderRadius="md"
                px={3}
                py={2}
                hasArrow
              >
                <span style={{ marginLeft: '6px' }}>
                  <Icon as={MdInfoOutline} boxSize={4} color="brand.400" />
                </span>
              </Tooltip>
            )}
            {fieldConfig.caption}
          </FormLabel>
        </Flex>
      )}
      {renderField()}
    </Box>
  );
};

export default DynamicFormField;
